import { isAlive, getParent, getType, onAction, hasParent, types } from "mobx-state-tree";
/**
 * This creates the definition for a type field in MST.
 * The field is optional so it doesn't have to be specified when creating
 * an instance.
 *
 * @param typeName the type
 * @returns
 */
export function typeField(typeName) {
    return types.optional(types.literal(typeName), typeName);
}
/**
 * Returns an ancestor of a node whose type name is `typeName`, if any.
 * This is like `getParentOfType(target, type)`, but allows us not to refer directly to the
 * parent type, which can cause circular reference errors in MST.
 */
export function getParentWithTypeName(target, typeName) {
    let current = target;
    while (hasParent(current)) {
        const parent = getParent(current);
        const type = getType(parent);
        if (type.name === typeName)
            return parent;
        current = parent;
    }
    return undefined;
}
export function getDocumentContentFromNode(target) {
    return getParentWithTypeName(target, "DocumentContent");
}
export function getContentIdFromNode(target) {
    var _a;
    return (_a = getDocumentContentFromNode(target)) === null || _a === void 0 ? void 0 : _a.contentId;
}
export function getTileContentById(target, tileId) {
    var _a;
    return (_a = getDocumentContentFromNode(target)) === null || _a === void 0 ? void 0 : _a.getTileContent(tileId);
}
export function getTileModelById(target, tileId) {
    var _a;
    return (_a = getDocumentContentFromNode(target)) === null || _a === void 0 ? void 0 : _a.getTile(tileId);
}
/**
 * A short circuit isAlive check. It is intended to be used in observing
 * components. If the observing component is working with a MST object that
 * might get destroyed and the component should not be rendered after the object
 * is destroyed, this function should prevent the MST warnings. These warnings
 * can show up when MobX recomputes a computed value to see if it has changed.
 * This recomputing can happen even if the component is never re-rendered.
 *
 * It doesn't throw an error since often these issues are not critical. However
 * the issues do have the potential to cause hard to track down problems.
 *
 * It doesn't return a value because it can be used before hooks in a component
 * which should be run regardless of this check for consistency.
 *
 * See mst-detached-error.md and mobx-react-mst.test.tsx for more details.
 */
export function verifyAlive(target, source = "unknown") {
    if (!isAlive(target)) {
        console.warn(`Destroyed MST Object is being accessed from ${source}. Type: ${getType(target)}`);
    }
}
/**
 * Identical to onAction (above) except defaults to { afterAttach: true, allActions: true }
 *
 * @param target
 * @param listener
 * @param options object that controls the behavior
 * @param options.attachAfter (default false) fires the listener *after* the action has executed instead of before.
 * @param options.onlyOuter (default true) only fires the listener for outermost actions
 * @returns
 */
export function onAnyAction(target, listener, options) {
    return onAction(target, listener, Object.assign({ attachAfter: true, allActions: true }, options));
}
//# sourceMappingURL=data:application/json;base64,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